import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { DelayedDataSheet } from 'shared/data-display/DelayedDataSheet';
import { cellClassNames, cellFormatTag } from 'shared/lib/cell-format';
import { cellStylingClassNames } from 'shared/lib/cell-styling';
import { replaceLineBreaks } from 'shared/lib/helper-functions';
import { localizedCellValue } from 'shared/lib/cell-locale';

// The tag data this component receives is opaque here (the tag domain lives in
// `slate`, which `shared` may not import — boundary: shared < entities < app).
// It is declared as a `type` (not interface) with an index signature so it stays
// structurally assignable to the caller-side tag-data shape across the injection
// boundary. `origin` is required so the resolver a caller supplies can forward it
// to the tag-domain colour helpers; only `conceptId` is read here.
type TagColorData = {
  conceptId?: string;
  origin: 'human' | 'ai';
  [key: string]: unknown;
};

// Tag colours are a tag-domain (app) concern; inject the resolver so this
// shared component never imports `app/` (boundary: shared < entities < app).
// Tag-rendering callers pass one; plain data-sheet callers omit it.
export type TagColorResolver = (highlightedOption: string, cellTagData: TagColorData | undefined) => { highlight: string; indicator: string };

interface Cell {
  value?: string;
  readOnly?: boolean;
  locales?: Record<string, string>;
  format?: string;
  background?: string;
  border?: string;
  styling?: string;
}

// The props react-datasheet's DataCell hands to a cell renderer. Only the fields
// consumed below are named; everything else (event handlers, style, spans,
// selected, …) is forwarded verbatim onto the rendered <td>/<th> via `...rest`,
// so it is captured by the index signature.
type CellRendererProps = {
  row: number;
  col: number;
  cell: Cell;
  children?: ReactNode;
  className?: string;
  attributesRenderer?: unknown;
  updated?: boolean;
  editing?: boolean;
  [key: string]: unknown;
};

// The react-datasheet instance `onCellsChanged` is invoked against.
interface DataSheetContext {
  xlsData: Cell[][];
  setState: (state: { grid: Cell[][] }) => void;
}

type CellChange = { cell: Cell; row: number; col: number; value: string };

interface HighlightedCell {
  row: number;
  col: number;
}

interface TaggedCell {
  cellRow: number;
  cellCol: number;
  tagId: string;
}

interface CalcPickMode {
  parentConceptId?: string;
  termConceptIds?: Set<string>;
  pickedTagIds?: Set<string>;
}

interface StyledDataSheetProps {
  locale?: string;
  taggedCells?: TaggedCell[];
  highlightedCells?: HighlightedCell[];
  showOriginalValue?: boolean;
  cssClass?: string;
  onTagClick?: (tagIds: string[]) => void;
  withTags?: boolean;
  highlightedOption?: string;
  tagDataMap?: Record<string, TagColorData>;
  calcPickMode?: CalcPickMode | null;
  termHighlightTagIds?: Set<string>;
  resolveTagColors?: TagColorResolver;
  [key: string]: unknown;
}

function StyledDataSheet(props: StyledDataSheetProps): ReactElement {
  const {
    locale,
    taggedCells,
    highlightedCells,
    showOriginalValue,
    cssClass,
    onTagClick,
    withTags,
    highlightedOption,
    tagDataMap,
    calcPickMode,
    termHighlightTagIds,
    resolveTagColors,
    ...rest
  } = props;

  // Stable renderer identities; a new identity remounts every cell each render.
  const memoValueRenderer = useMemo(() => valueRenderer(locale, showOriginalValue), [locale, showOriginalValue]);
  const memoCellRenderer = useCallback(
    (cellProps: CellRendererProps) =>
      cellRenderer(
        cellProps,
        taggedCells,
        highlightedCells,
        onTagClick,
        withTags,
        highlightedOption,
        tagDataMap,
        calcPickMode,
        termHighlightTagIds,
        resolveTagColors
      ),
    [taggedCells, highlightedCells, onTagClick, withTags, highlightedOption, tagDataMap, calcPickMode, termHighlightTagIds, resolveTagColors]
  );

  return (
    <DelayedDataSheet
      className={`table--reference-data${cssClass ? ' ' + cssClass : ''}`}
      overflow="wrap"
      valueRenderer={memoValueRenderer}
      cellRenderer={memoCellRenderer}
      valueViewer={valueViewer}
      onCellsChanged={onCellsChanged}
      onContextMenu={onContextMenu}
      {...rest}
      readOnly
    />
  );
}

export function onCellsChanged(this: DataSheetContext, changes: CellChange[]): void {
  const grid = this.xlsData;
  changes.forEach(({ row, col, value }) => {
    grid[row][col] = { ...grid[row][col], value };
  });
  this.setState({ grid });
}

export function onContextMenu(e: React.MouseEvent, cell: Cell, _i: number, _j: number): void {
  if (cell.readOnly) e.preventDefault();
}

export function valueRenderer(locale?: string, showOriginalValue?: boolean) {
  return function valueRenderer(cell: Cell): string | undefined {
    return localizedCellValue(cell, locale, showOriginalValue);
  };
}

export function valueViewer(cell: Cell): ReactNode {
  return replaceLineBreaks(cell.value ?? '');
}

function TagIndicator({ style }: { style?: React.CSSProperties }) {
  return (
    <div
      className="table-tag-indicator"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
    >
      <span style={{ position: 'absolute', top: '0', left: '0', height: '100%', borderRadius: '4px 0 0 4px', width: '4px', ...style }} />
      <span style={{ position: 'absolute', top: '0', right: '0', height: '100%', borderRadius: '0 4px 4px 0', width: '4px', ...style }} />
    </div>
  );
}

export function cellRenderer(
  props: CellRendererProps,
  taggedCells?: TaggedCell[],
  highlightedCells?: HighlightedCell[],
  onTagClick?: (tagIds: string[]) => void,
  withTags?: boolean,
  highlightedOption?: string,
  tagDataMap?: Record<string, TagColorData>,
  calcPickMode?: CalcPickMode | null,
  termHighlightTagIds?: Set<string>,
  resolveTagColors?: TagColorResolver
): ReactElement {
  const {
    row,
    col,
    cell,
    children: _children,
    attributesRenderer: _attributesRenderer,
    updated: _updated,
    editing: _editing,
    className: _className,
    ...rest
  } = props;

  // Check if cell is tagged — collect ALL tags for this cell
  const cellTags = taggedCells?.filter((tag) => row === tag.cellRow && col === tag.cellCol) || [];
  const cellTag = cellTags[0];
  const isTagged = cellTags.length > 0;
  const cellTagIds = cellTags.map((t) => t.tagId);

  // Check if cell is highlighted (for backwards compatibility)
  const isHighlighted = !!highlightedCells?.find((highlight) => row === highlight.row && col === highlight.col);

  const classNames = [_className, cellClassNames(cell), cell.background, cell.border];

  if (isHighlighted && !isTagged) {
    classNames.push('bg-warning');
  }

  const className = classNames.join(' ');
  const cellTagData = isTagged ? tagDataMap?.[cellTag.tagId] : undefined;

  const baseColors = resolveTagColors?.(highlightedOption ?? '', cellTagData) ?? { highlight: '', indicator: '' };
  let highlightColor = baseColors.highlight;
  let indicatorColor = baseColors.indicator;
  if (isTagged && withTags) {
    const conceptId = cellTagData?.conceptId;
    const isPickParent = !!calcPickMode && !!conceptId && calcPickMode.parentConceptId === conceptId;
    const isPickedTerm = !!calcPickMode && !!conceptId && calcPickMode.termConceptIds?.has(conceptId);
    const isPickedCell = isPickedTerm && cellTagIds.some((id) => calcPickMode?.pickedTagIds?.has(id));
    const isTermHighlighted = !calcPickMode && cellTagIds.some((id) => termHighlightTagIds?.has(id));
    if (isPickedCell || isTermHighlighted) {
      highlightColor = '#c4e8c9';
      indicatorColor = '#3d7a4f';
    } else if (isPickedTerm) {
      highlightColor = '#e8f3ea';
      indicatorColor = '#a8cbb0';
    } else if (calcPickMode && !isPickParent) {
      highlightColor = '#fdebc8';
      indicatorColor = '#d9a03f';
    }
  }

  // Style for tagged cells - use dynamic color based on highlighted option
  const taggedStyle: React.CSSProperties =
    isTagged && withTags
      ? {
          backgroundColor: `${highlightColor}`,
          position: 'relative',
          borderRadius: '4px',
        }
      : {};

  const handleCellClick = isTagged && withTags ? () => onTagClick?.(cellTagIds) : undefined;
  const tagDataAttrs = isTagged && withTags ? { 'data-tag-ids': cellTagIds.join(' ') } : {};

  const renderContent = (stylingClasses: string) => (
    <>
      {isTagged && withTags && <TagIndicator style={{ backgroundColor: indicatorColor }} />}
      {stylingClasses ? <span className={stylingClasses}>{props.children}</span> : props.children}
    </>
  );

  const stylingClasses = cellStylingClassNames(cell.styling);

  if (cellFormatTag(cell.format) === 'th') {
    return (
      <th className={className} {...rest} style={taggedStyle} onMouseUp={handleCellClick} {...tagDataAttrs}>
        {renderContent(stylingClasses)}
      </th>
    );
  }

  return (
    <td className={className} {...rest} style={taggedStyle} onMouseUp={handleCellClick} {...tagDataAttrs}>
      {renderContent(stylingClasses)}
    </td>
  );
}

export default StyledDataSheet;
